import React from 'react';
import * as Sentry from '@sentry/react';

interface State {
  error: Error | null;
}

/**
 * App-level error boundary. Without this, an uncaught error in any component
 * (Monaco, FileExplorer, etc.) would unmount the entire React tree and the
 * student would see a blank page. We catch here, log to console (Sentry can
 * hook in later), and offer the user a way to recover without losing routing
 * context.
 */
export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught:', error, info);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 24,
            gap: 16,
            fontFamily: 'system-ui, sans-serif',
            background: '#1e1e1e',
            color: '#eee',
          }}
        >
          <div style={{ fontSize: 48 }}>💥</div>
          <h1 style={{ margin: 0 }}>Something went wrong</h1>
          <p style={{ maxWidth: 520, textAlign: 'center', opacity: 0.85 }}>
            The IDE hit an unexpected error. Your saved code is unaffected — try
            reloading. If it keeps happening, contact your instructor with the
            details from the browser console.
          </p>
          <pre
            style={{
              maxWidth: 720,
              maxHeight: 240,
              overflow: 'auto',
              background: '#111',
              padding: 12,
              borderRadius: 8,
              fontSize: 12,
              opacity: 0.75,
            }}
          >
            {this.state.error.message}
          </pre>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => this.setState({ error: null })}
              style={{ padding: '8px 16px', cursor: 'pointer' }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '8px 16px', cursor: 'pointer' }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
